import { useState } from "react";
import { motion } from "framer-motion";
import { Play, ExternalLink, Save, RefreshCw, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { coverUrl, COVER_STYLES } from "../lib/api";
import { getMoodMeta, formatDuration, formatMinSec } from "../lib/moods";

export default function PlaylistResult({ generation, onRegenerate, onSaved }) {
  const [name, setName] = useState(
    `${getMoodMeta(generation).name} · ${generation.duration_minutes}min`
  );
  const [editingName, setEditingName] = useState(false);
  const [saving, setSaving] = useState(false);
  const [coverStyle, setCoverStyle] = useState(generation.cover_style || "disco");
  const [saved, setSaved] = useState(generation.saved_playlist_id || null);
  const [embedUrl, setEmbedUrl] = useState(
    generation.saved_playlist_id
      ? `https://open.spotify.com/embed/playlist/${generation.saved_playlist_id}`
      : null
  );
  const [playlistUrl, setPlaylistUrl] = useState(generation.saved_playlist_url || null);

  const meta = getMoodMeta(generation);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/playlist/save", { generation_id: generation.id, name, cover_style: coverStyle });
      setSaved(r.data.playlist_id);
      setEmbedUrl(r.data.embed_url);
      setPlaylistUrl(r.data.playlist_url);
      if (r.data.cover_uploaded) toast.success("Playlist sauvegardée sur Spotify avec sa cover");
      else toast.success("Playlist sauvegardée — reconnecte-toi à Spotify pour activer les covers");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.section
      data-testid="playlist-result"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl overflow-hidden bg-[#121212]"
    >
      {/* Spotify-style header with gradient from mood color */}
      <div
        className="relative px-6 md:px-8 pt-10 pb-8"
        style={{
          background: `linear-gradient(180deg, ${meta.color} 0%, rgba(255,46,147,0.3) 40%, #121212 100%)`,
        }}
      >
        <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start md:items-end">
          <img
            data-testid="playlist-cover"
            src={coverUrl(generation.id, coverStyle)}
            alt={meta.name}
            className="shrink-0 h-48 w-48 md:h-56 md:w-56 rounded-md object-cover shadow-[0_8px_30px_rgba(0,0,0,0.55)]"
          />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase text-white/90 tracking-wider">Playlist</div>
            {editingName ? (
              <input
                data-testid="playlist-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setEditingName(false)}
                onKeyDown={(e) => e.key === "Enter" && setEditingName(false)}
                autoFocus
                className="mt-2 w-full bg-transparent border-b border-white/40 focus:border-white outline-none font-black text-4xl md:text-6xl text-white tracking-tight"
              />
            ) : (
              <h1
                onClick={() => setEditingName(true)}
                className="mt-2 font-black text-4xl md:text-6xl text-white tracking-tight cursor-text hover:opacity-90 break-words"
                data-testid="playlist-title"
                title="Cliquer pour renommer"
              >
                {name}
              </h1>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-white/90 font-semibold">
              <span>Glimpse</span>
              <span>·</span>
              <span data-testid="result-track-count">{generation.tracks.length} titres</span>
              <span className="text-white/60">·</span>
              <span data-testid="result-duration" className="text-white/70 font-medium">
                {formatDuration(generation.total_ms)}
              </span>
              <span className="text-white/60">·</span>
              <span className="text-white/70 font-medium">cible {generation.duration_minutes}min (±2)</span>
              {generation.mood2 && (
                <span data-testid="result-blend" className="text-white/70 font-medium">
                  · mix {100 - generation.blend}/{generation.blend}
                </span>
              )}
              {generation.exclude_recent && (
                <span data-testid="result-excluded" className="text-white/70 font-medium">
                  · {generation.recent_fallback ? "pas assez de titres pour éviter les récents" : `${generation.excluded_count} titres récents évités`}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action bar */}
      <div className="bg-gradient-to-b from-black/40 to-transparent px-6 md:px-8 py-5 flex flex-wrap items-center gap-4">
        <button
          data-testid="save-playlist-btn"
          onClick={save}
          disabled={saving || !!saved}
          className="sp-play-btn h-14 w-14 rounded-full flex items-center justify-center shadow-xl disabled:opacity-70"
          title={saved ? "Sauvegardée" : "Sauver sur Spotify"}
        >
          {saving ? <Loader2 className="h-6 w-6 animate-spin text-black" /> : saved ? <ExternalLink className="h-5 w-5" /> : <Save className="h-5 w-5" />}
        </button>
        <button
          data-testid="regenerate-btn"
          onClick={onRegenerate}
          className="text-[#B3B3B3] hover:text-white font-semibold text-sm flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" /> Regénérer
        </button>
        {playlistUrl && (
          <a
            data-testid="open-spotify-link"
            href={playlistUrl}
            target="_blank"
            rel="noreferrer"
            className="ml-auto text-[#B3B3B3] hover:text-white text-sm font-semibold inline-flex items-center gap-1"
          >
            Ouvrir dans Spotify <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>

      {!saved && (
        <div className="px-6 md:px-8 pb-6">
          <div className="text-xs font-bold uppercase tracking-widest text-[#B3B3B3] mb-3">Style de cover</div>
          <div data-testid="cover-style-picker" className="flex flex-wrap gap-3">
            {COVER_STYLES.map((st) => (
              <button
                key={st.key}
                data-testid={`cover-style-${st.key}`}
                onClick={() => setCoverStyle(st.key)}
                className={`group text-center rounded-md p-1.5 transition-colors ${coverStyle === st.key ? "bg-[#282828] sp-mood-active" : "hover:bg-[#1f1f1f]"}`}
              >
                <img src={coverUrl(generation.id, st.key)} alt={st.name} loading="lazy" className="h-20 w-20 md:h-24 md:w-24 rounded object-cover" />
                <div className={`mt-1.5 text-xs font-bold ${coverStyle === st.key ? "text-[#FF2E93]" : "text-[#B3B3B3] group-hover:text-white"}`}>{st.name}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {embedUrl && (
        <div className="px-6 md:px-8 pb-4">
          <iframe
            data-testid="spotify-embed"
            src={embedUrl}
            width="100%"
            height="152"
            frameBorder="0"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            title="Spotify player"
            className="rounded-xl"
          />
        </div>
      )}

      {/* Tracklist Spotify-like */}
      <div className="px-6 md:px-8 pb-8">
        <div className="grid grid-cols-[20px_minmax(0,1fr)_80px] md:grid-cols-[20px_minmax(0,1fr)_minmax(0,1fr)_80px] gap-4 items-center text-xs uppercase tracking-widest text-[#6a6a6a] border-b border-[#282828] py-2 px-2">
          <span>#</span>
          <span>Titre</span>
          <span className="hidden md:block">Album</span>
          <span className="flex justify-end"><Clock className="h-3.5 w-3.5" /></span>
        </div>
        <ul data-testid="track-list" className="mt-1">
          {generation.tracks.map((t, i) => (
            <li
              key={t.id + i}
              className="sp-row grid grid-cols-[20px_minmax(0,1fr)_80px] md:grid-cols-[20px_minmax(0,1fr)_minmax(0,1fr)_80px] gap-4 items-center rounded-md px-2 py-2 group"
            >
              <span className="text-sm text-[#B3B3B3] group-hover:hidden tabular-nums text-center">{i + 1}</span>
              <Play className="h-4 w-4 text-white hidden group-hover:block" fill="white" />
              <div className="flex items-center gap-3 min-w-0">
                {t.album_image ? (
                  <img src={t.album_image} alt="" className="h-10 w-10 rounded object-cover shrink-0" />
                ) : (
                  <div className="h-10 w-10 rounded bg-[#282828] shrink-0" />
                )}
                <div className="min-w-0">
                  <div className="truncate text-white font-medium">{t.name}</div>
                  <div className="truncate text-xs text-[#B3B3B3]">
                    {t.artists.map((a) => a.name).join(", ")}
                  </div>
                </div>
              </div>
              <div className="hidden md:block truncate text-sm text-[#B3B3B3]">{t.album_name || ""}</div>
              <div className="text-sm text-[#B3B3B3] text-right tabular-nums">{formatMinSec(t.duration_ms)}</div>
            </li>
          ))}
        </ul>
      </div>
    </motion.section>
  );
}
